import { UI, HStack, Stack, VStack } from '@genial/components';
import { Color, Spacing, Typography } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { IconButton } from 'native-base';
import React, { useEffect } from 'react';
import { FlatList } from 'react-native';

import { useScrollCloseToBottomContext } from '../../../../../contexts/ScrollCloseToBottom';
import { NormalizedDistributionProduct } from '../../../../../store/managementAnalysis/types';
import { NotFoundTable, messageError } from '../DynamicProductTable/constants';
import { IndicatorIcon } from '../IndicatorIcon';
import { LateralProductBar } from '../LateralProductBar';

interface ProductFieldProps<T> {
  key: keyof T;
  label: string;
  indicatorKey?: keyof T;
}

export interface ProductTableTabletProps<T> {
  data: T[];
  isLoading: boolean;
  isLoadingMore: boolean;
  fields: ProductFieldProps<T>[];
  columns: number;
  product: NormalizedDistributionProduct;
  handleEndReached: () => void;
  SecondaryInfo?: React.FC<{ item: T }>;
}

export const ProductTableTablet = <T,>({
  data,
  fields,
  columns,
  product,
  isLoading,
  isLoadingMore,
  SecondaryInfo,
  handleEndReached,
}: ProductTableTabletProps<T>) => {
  const [isOpen, setIsOpen] = React.useState<number[]>([]);
  const rows = Math.ceil(fields.length / columns);

  const { isCloseToBottom } = useScrollCloseToBottomContext();

  const renderItem = (label: string, value: string | number = messageError, indicator?: number) => {
    const valueComponent = (
      <UI.Text isLoading={isLoading} fontSize={Typography['fontsize.xs']} variant="semibold">
        {`${value}`}
      </UI.Text>
    );

    return (
      <VStack
        alignContent="center"
        space={Spacing['inline.quarck']}
        w={rows !== 1 ? `${100 / columns}%` : undefined}>
        <UI.Text fontSize={Typography['fontsize.xs']} color="neutral.dark">
          {label}
        </UI.Text>

        {indicator !== undefined ? (
          <HStack>
            <IndicatorIcon percentage={indicator} />
            {valueComponent}
          </HStack>
        ) : (
          valueComponent
        )}
      </VStack>
    );
  };

  const renderLoadingItem = (label: string) => (
    <VStack alignContent="center" space={Spacing['inline.quarck']}>
      <UI.Text fontSize={Typography['fontsize.xs']} color="neutral.dark">
        {label}
      </UI.Text>
      <UI.Text isLoading fontSize={Typography['fontsize.xs']} />
    </VStack>
  );

  const renderProductItem = (item: T, index: number) => {
    const indexOpen = isOpen.includes(index);
    const fieldsToShow = indexOpen ? fields : fields.slice(0, columns);

    return (
      <UI.Shadow
        testId="shadow"
        level={1}
        borderRadius="8px"
        p={Spacing['inset.sm']}
        key={index.toString()}
        position="relative">
        <LateralProductBar backgroundColor={Color[product.color]} />
        <HStack space={Spacing['inline.quarck']}>
          <FlatList
            key={`${product.productClassName}-${index}`}
            style={{ overflow: 'visible' }}
            columnWrapperStyle={{
              justifyContent: rows === 1 ? 'space-between' : undefined,
              paddingVertical: Utils.Token.ToInt(Spacing, 'stack.nano'),
            }}
            keyExtractor={(field) => `${field.label}`}
            data={fieldsToShow}
            renderItem={({ item: field }) =>
              renderItem(
                field.label,
                item[field.key] as string,
                field.indicatorKey ? (item[field.indicatorKey] as number) : undefined
              )
            }
            numColumns={columns}
          />
          <IconButton
            p={0}
            h={Spacing['inset.sm']}
            variant="unstyled"
            onPress={() =>
              setIsOpen(indexOpen ? [...isOpen.filter((i) => index !== i)] : [...isOpen, index])
            }
            icon={
              <UI.Icon
                name={indexOpen ? 'ChevronUp' : 'ChevronDown'}
                size={'xxs'}
                color={Color['neutral.black']}
              />
            }
          />
        </HStack>
        {SecondaryInfo && indexOpen && (
          <Stack py={Spacing['stack.nano']}>
            <SecondaryInfo item={item} />
          </Stack>
        )}
      </UI.Shadow>
    );
  };

  const renderProductItemLoading = (index: number) => (
    <UI.Shadow
      testId="shadow"
      level={1}
      borderRadius="8px"
      key={index.toString()}
      p={Spacing['inset.sm']}
      position="relative">
      <LateralProductBar backgroundColor={Color[product.color]} />
      <HStack space={Spacing['inline.quarck']} w="full">
        <HStack justifyContent="space-between" flex={1} pr={Spacing['inline.xxxs']}>
          {fields
            .slice(0, columns)
            .map((field: ProductFieldProps<T>) => renderLoadingItem(field.label))}
        </HStack>
        <IconButton
          p={0}
          h={Spacing['inset.sm']}
          variant="unstyled"
          disabled
          icon={<UI.Icon name={'ChevronDown'} size={'xxs'} color={Color['neutral.medium']} />}
        />
      </HStack>
    </UI.Shadow>
  );

  useEffect(() => {
    if (isCloseToBottom && !isLoading) {
      handleEndReached();
    }
  }, [isCloseToBottom, isLoading, handleEndReached]);

  if (isLoading) {
    return (
      <VStack space={Spacing['stack.xxxs']}>
        {Array.from({ length: 3 }).map((_, index) => renderProductItemLoading(index))}
      </VStack>
    );
  }

  if (data?.length === 0 && !isLoading) {
    return <NotFoundTable />;
  }

  return (
    <VStack space={Spacing['stack.xxxs']} pb={Spacing['stack.xxxs']}>
      {data.map((item, index) => renderProductItem(item, index))}
      {!!isLoadingMore && renderProductItemLoading(data.length)}
    </VStack>
  );
};
